import { ApiError, apiFetch, getToken } from './apiFetch';
import type {
  DefaultPeriodResponse,
  EmployeePayrollDetail,
  InternalNotesMap,
  OneTimeBonusMode,
  PayrollReportResponse,
  PeriodSelection,
} from '@salaries/shared';

function periodParams(sel: PeriodSelection): URLSearchParams {
  const q = new URLSearchParams();
  q.set('kind', sel.kind);
  if (sel.kind === 'custom') {
    q.set('start_date', sel.start_date);
    q.set('end_date', sel.end_date);
  } else if (sel.kind === 'month') {
    q.set('year', String(sel.year));
    q.set('month', String(sel.month));
  }
  return q;
}

/** Which month the report should open on (last month vs this month — see
 *  @salaries/shared/default-period for the rules). Cheap; ask before /report. */
export async function getDefaultPeriod(): Promise<DefaultPeriodResponse> {
  return apiFetch<DefaultPeriodResponse>('/payroll/default-period');
}

/** `unitId` scopes the report to one P&L unit (a real box status: frozen when
 *  that unit is closed, live otherwise). Omitted = the combined view across
 *  every unit (identical to the box-wide report for a box that doesn't split). */
export async function getPayrollReport(
  sel: PeriodSelection,
  unitId?: number,
): Promise<PayrollReportResponse> {
  const q = periodParams(sel);
  if (unitId != null) q.set('pnl_unit_id', String(unitId));
  return apiFetch<PayrollReportResponse>(`/payroll/report?${q.toString()}`);
}

/** A progress line from the streamed report. `through` = the latest class date
 *  fetched so far (Arbox pages the month in date order); `data` (when present)
 *  is a PARTIAL report built from those rows — for display only. */
export interface PayrollReportProgress {
  pages: number;
  through: string | null;
  data?: PayrollReportResponse;
}

/** The same report as getPayrollReport, streamed: `onProgress` gets page
 *  progress + partial previews while Arbox pages in; resolves with the real,
 *  complete report. Throws ApiError exactly like apiFetch on failure.
 *  `refresh` = pull Arbox live instead of the server's saved copy. */
export async function streamPayrollReport(
  sel: PeriodSelection,
  unitId: number | undefined,
  opts: { refresh?: boolean },
  onProgress: (p: PayrollReportProgress) => void,
  signal?: AbortSignal,
): Promise<PayrollReportResponse> {
  const q = periodParams(sel);
  if (unitId != null) q.set('pnl_unit_id', String(unitId));
  if (opts.refresh) q.set('refresh', '1');
  q.set('stream', '1');
  const token = getToken();
  const res = await fetch(`/api/payroll/report?${q.toString()}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    signal,
  });
  // Validation/auth failures happen before the stream starts: plain JSON.
  if (!res.ok || !res.body) {
    const body = await res.json().catch(() => null);
    throw new ApiError(body?.error || `HTTP ${res.status}`, res.status);
  }

  const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
  let buf = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (value) buf += value;
    let nl: number;
    while ((nl = buf.indexOf('\n')) >= 0) {
      const line = buf.slice(0, nl).trim();
      buf = buf.slice(nl + 1);
      if (!line) continue;
      const e = JSON.parse(line);
      if (e.type === 'done') return e.data as PayrollReportResponse;
      if (e.type === 'error') throw new ApiError(e.error, e.status ?? 500);
      if (e.type === 'progress') onProgress({ pages: e.pages, through: e.through });
      else if (e.type === 'partial') onProgress({ pages: e.pages, through: e.through, data: e.data });
    }
    if (done) break;
  }
  // The stream ended without the final report — never show the partial as done.
  throw new ApiError('החיבור נקטע לפני שהדוח הושלם. נסו שוב.', 0);
}

/** Fetch one staff member's full salary breakdown for the period (drawer). */
export async function getPayrollDetail(
  staffId: number,
  sel: PeriodSelection,
  unitId?: number,
): Promise<EmployeePayrollDetail> {
  const q = periodParams(sel);
  q.set('staff', String(staffId));
  if (unitId != null) q.set('pnl_unit_id', String(unitId));
  return apiFetch<EmployeePayrollDetail>(`/payroll/detail?${q.toString()}`);
}

/** Download one staff member's salary-breakdown .xlsx (auth'd blob → anchor). */
export async function downloadPayrollExcel(
  staffId: number,
  sel: PeriodSelection,
  unitId?: number,
): Promise<void> {
  const q = periodParams(sel);
  q.set('staff', String(staffId));
  if (unitId != null) q.set('pnl_unit_id', String(unitId));
  const res = await fetch(`/api/payroll/excel?${q.toString()}`, {
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  if (!res.ok) throw new Error('שגיאה בהפקת קובץ האקסל');
  const blob = await res.blob();
  const cd = res.headers.get('Content-Disposition') || '';
  const m = cd.match(/filename\*=UTF-8''([^;]+)/);
  const filename = m ? decodeURIComponent(m[1]!) : 'salary.xlsx';
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** The box-wide detailed workbook (summary + one row per class) for the report
 *  rows in view — each line is a (coach, P&L unit). */
export async function downloadDetailedPayrollExcel(
  sel: PeriodSelection,
  lines: { staff: number; unit: number }[],
): Promise<void> {
  const q = periodParams(sel);
  const res = await fetch(`/api/payroll/excel-detailed?${q.toString()}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ lines }),
  });
  if (!res.ok) throw new Error('שגיאה בהפקת קובץ האקסל');
  const blob = await res.blob();
  const cd = res.headers.get('Content-Disposition') || '';
  const m = cd.match(/filename\*=UTF-8''([^;]+)/);
  const filename = m ? decodeURIComponent(m[1]!) : 'salary_detailed.xlsx';
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export interface BatchStatus {
  total: number;
  pending: number;
  sent: number;
  skipped: number;
  failed: number;
}

/** Queue a background bulk send for the selected staff. Returns immediately (202);
 *  poll getBatchStatus for progress. */
export async function sendBatch(
  staffIds: number[],
  sel: PeriodSelection,
): Promise<{ count: number; period_start: string; period_end: string }> {
  return apiFetch('/payroll/send-batch', {
    method: 'POST',
    body: JSON.stringify({ ...sel, staff_ids: staffIds }),
  });
}

/** Current batch progress counts for the live toast. */
export async function getBatchStatus(sel: PeriodSelection): Promise<BatchStatus> {
  return apiFetch<BatchStatus>(`/payroll/jobs/status?${periodParams(sel).toString()}`);
}

/** Queue a single staff member's salary send for approval (returns immediately;
 *  the background processor renders + emails). Poll getSingleSendStatus. */
export async function sendPayroll(
  staffId: number,
  sel: PeriodSelection,
  /** The row's P&L unit — only that branch's line is sent. Omitted = all of the
   *  coach's units (the bulk-send behaviour). */
  unitId?: number,
): Promise<{ status: string }> {
  // Spread the whole selection so 'month' sends year+month (and 'custom' sends
  // the dates) — the server needs them to resolve the period, else it 400s with
  // "בחירת תקופה שגויה".
  const body = { arbox_users_boxes_id: staffId, pnl_unit_id: unitId, ...sel };
  return apiFetch<{ status: string }>('/payroll/send', { method: 'POST', body: JSON.stringify(body) });
}

/** Operator: revert a line to draft — cancels the send/approval, kills the old
 *  approval link, and clears the invoice. Re-send to issue a fresh link. */
export async function revertLine(
  staffId: number,
  sel: PeriodSelection,
  unitId?: number,
): Promise<void> {
  await apiFetch('/payroll/line/revert', {
    method: 'POST',
    body: JSON.stringify({ arbox_users_boxes_id: staffId, pnl_unit_id: unitId, ...sel }),
  });
}

/** Operator: clear just the invoice on a line (status untouched). */
export async function clearLineInvoice(
  staffId: number,
  sel: PeriodSelection,
  unitId?: number,
): Promise<void> {
  await apiFetch('/payroll/line/clear-invoice', {
    method: 'POST',
    body: JSON.stringify({ arbox_users_boxes_id: staffId, pnl_unit_id: unitId, ...sel }),
  });
}

export type SingleSendOutcome = 'pending' | 'sent' | 'skipped' | 'failed' | 'none';

/** Poll the outcome of a queued single send (for the live toast). */
export async function getSingleSendStatus(
  staffId: number,
  sel: PeriodSelection,
  unitId?: number,
): Promise<SingleSendOutcome> {
  const q = periodParams(sel);
  q.set('staff', String(staffId));
  if (unitId != null) q.set('unit', String(unitId));
  const r = await apiFetch<{ outcome: SingleSendOutcome }>(`/payroll/send-status?${q.toString()}`);
  return r.outcome;
}

/** Fetch the uploaded receipt as an object URL for the report's hover preview.
 *  `unitId` is the row's P&L unit — a coach at several branches has a separate
 *  invoice per branch, and each row must open its own. */
export async function fetchReceiptObjectUrl(
  staffId: number,
  sel: PeriodSelection,
  unitId?: number,
): Promise<{ url: string; contentType: string }> {
  const q = periodParams(sel);
  q.set('staff', String(staffId));
  if (unitId != null) q.set('unit', String(unitId));
  const res = await fetch(`/api/payroll/receipt?${q.toString()}`, {
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  if (!res.ok) throw new Error('שגיאה בטעינת החשבונית');
  const blob = await res.blob();
  return { url: URL.createObjectURL(blob), contentType: blob.type };
}

/**
 * Open a staff member's Arbox profile in a new tab. The blank tab is opened
 * synchronously on the click gesture (so the iframe sandbox / popup-blockers
 * don't swallow it), then redirected once the URL resolves server-side.
 */
export async function openArboxProfile(staffId: number): Promise<void> {
  const tab = window.open('', '_blank');
  try {
    const { profile_link } = await apiFetch<{ profile_link: string | null }>(
      `/payroll/staff-profile-link/${staffId}`,
    );
    if (profile_link && tab) tab.location.href = profile_link;
    else tab?.close();
  } catch {
    tab?.close();
  }
}

/** The body for either hand-entered adjustment on a payroll line — the one-time
 *  bonus or the one-time deduction. They're the same shape and the same rules;
 *  only the endpoint differs. */
export interface LineAdjustmentBody {
  arbox_users_boxes_id: number;
  period_start: string;
  period_end: string;
  /** The P&L unit whose line this belongs to (0 when the box isn't split). Both
   *  adjustments are per line, so each stays on the branch that entered it. */
  pnl_unit_id: number;
  /** ₪ — applies when mode is 'amount'. Sent in both modes so switching back keeps it. */
  amount: number;
  mode: OneTimeBonusMode;
  /** % of the coach's other salary components — applies when mode is 'percent'. */
  percent: number;
  /** The operator's reason, shown to the coach on their payslip. null clears it. */
  note: string | null;
}

/** Back-compat alias for the bonus's original body name. */
export type OneTimeBonusBody = LineAdjustmentBody;

type LineAdjustmentResult = {
  amount: number;
  mode: OneTimeBonusMode;
  percent: number;
  note: string | null;
};

export async function updateOneTimeBonus(
  body: LineAdjustmentBody,
): Promise<LineAdjustmentResult> {
  return apiFetch<LineAdjustmentResult>('/payroll/one-time-bonus', {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export async function updateDeduction(body: LineAdjustmentBody): Promise<LineAdjustmentResult> {
  return apiFetch<LineAdjustmentResult>('/payroll/deduction', {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

/** Every private operator note for a period, keyed "<coach>:<unit>". Never part
 *  of the report payload — the coach must not see these. */
export async function getInternalNotes(periodStart: string, periodEnd: string): Promise<InternalNotesMap> {
  const qs = new URLSearchParams({ period_start: periodStart, period_end: periodEnd });
  return apiFetch<InternalNotesMap>(`/payroll/internal-notes?${qs}`);
}

export async function updateInternalNote(body: {
  arbox_users_boxes_id: number;
  period_start: string;
  period_end: string;
  pnl_unit_id: number;
  note: string | null;
}): Promise<{ note: string | null }> {
  return apiFetch<{ note: string | null }>('/payroll/internal-note', {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}
